/**
 * FolderPhoto（后端）→ PhotoItem（前端）字段适配
 *
 * 数据源有两个，返回结构一致（GetFolderPhotoResponse · FolderPhoto）：
 *  · /web/folder/photo/get（常规文件夹详情页）
 *  · /web/folder/ai/unclassified/photos（待归档虚拟文件夹详情页，2026-05-28 v2 对齐）
 *
 * 两页共用同一份映射，避免重复实现。新增 / 改字段只动本文件。
 */

import type { PhotoItem } from '../PhotoGrid'

/**
 * 后端单条 photo · 仅声明前端用到的字段
 *
 * 字段命名按 /web/folder/photo/get 实际返回（多为小写）：
 *  · smallurl / largeurl 小写；个别接口可能直接返大写，都兜底
 *  · time 为字符串（'YYYY-MM-DD HH:mm:ss' / 'YYYY-MM-DD HH:mm'）或毫秒数
 *  · seq 缺失时由 id 兜底（见 adaptFolderPhoto）
 */
export interface BackendPhoto {
  id: string | number
  /** 小图 URL · 后端原字段（小写） */
  smallurl?: string
  largeurl?: string
  /** 大写兜底 · 个别新接口可能直接返大写 */
  smallURL?: string
  largeURL?: string
  /** 拍摄时间 · 字符串或毫秒数；adaptFolderPhoto 统一归一为毫秒 */
  time: string | number
  name?: string
  /** 地点 · 与 addr 同义，命名兼容 */
  location?: string
  badge?: string
  /** 原图文件名 / URL · 批量动作去重 key（按 URL） */
  file?: string
  /** 同相册内稳定序号 · photoPicker / deletePhoto 等去重 key */
  seq?: string | number
  /** 拍摄人昵称 · 不同接口字段名候选 */
  userNickname?: string
  nickname?: string
  /** 水印地址文本 */
  addr?: string
  /** 媒体类型 · 0-图片 1-视频；缺省视作图片（见 adaptFolderPhoto） */
  mediaType?: number
  /** 视频地址 · mediaType=1 时大图预览 / 水印请求用 */
  videoURL?: string
  /** 拍摄人 userID · 预览"添加到文件夹"按钮判断是否本人照片用 */
  userID?: string
}

/**
 * 把后端单条 photo 适配成前端 PhotoItem。
 *
 * @param raw - 后端原始 photo（FolderPhoto 结构）
 * @returns 前端渲染用 PhotoItem（时间归一为毫秒、URL / 昵称 / 地点字段兜底）
 *
 * @example
 * ```ts
 * adaptFolderPhoto({ id: '1', smallurl: 'a.jpg', time: '2025-11-30 16:53:49' })
 * // → { id: '1', smallURL: 'a.jpg', time: 1764492829000, seq: 1, ... }
 * ```
 */
export function adaptFolderPhoto(raw: BackendPhoto): PhotoItem {
  const rawTime = raw.time
  // 字符串时间把空格换成 T 再 Date.parse；数字时间视作已是毫秒
  const ms =
    typeof rawTime === 'number'
      ? rawTime
      : Date.parse(String(rawTime).replace(' ', 'T'))
  // seq 缺失时用 id 兜底（与旧 redux flow `seq: Number(item.id)` 一致）
  const fallbackSeq = (() => {
    if (raw.seq != null) return raw.seq
    const n = Number(raw.id)
    return Number.isFinite(n) ? n : undefined
  })()
  return {
    id: String(raw.id),
    smallURL: raw.smallurl || raw.smallURL,
    largeURL: raw.largeurl || raw.largeURL,
    time: Number.isFinite(ms) ? ms : 0,
    name: raw.name,
    badge: raw.badge,
    file: raw.file,
    seq: fallbackSeq,
    userNickname: raw.userNickname || raw.nickname,
    addr: raw.addr || raw.location,
    // 缺省视作图片(0) · ViewerV2 据此选 largeURL / videoURL 发起水印请求
    mediaType: raw.mediaType ?? 0,
    videoURL: raw.videoURL,
    userID: raw.userID
  }
}
